import React from "react";

const styles = {
    page: {
        height: "100vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "#f5f6f8",
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        color: "#1f2937",
    },
    hero: {
        background: "linear-gradient(135deg, #111827 0%, #1f2a44 100%)",
        color: "#fff",
        padding: "56px 24px 40px",
        textAlign: "center",
    },
    heroBadge: {
        display: "inline-block",
        fontSize: 12,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "rgba(255,255,255,0.12)",
        padding: "4px 12px",
        borderRadius: 999,
        marginBottom: 14,
    },
    heroTitle: {
        fontSize: 32,
        fontWeight: 700,
        margin: 0,
    },
    heroSub: {
        marginTop: 10,
        fontSize: 14,
        color: "#c7cbd4",
    },
    container: {
        maxWidth: 880,
        margin: "-28px auto 80px",
        padding: "0 20px",
    },
    card: {
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
        padding: "36px 40px 44px",
    },
    lead: {
        fontSize: 15.5,
        color: "#374151",
        marginBottom: 8,
    },
    h2: {
        fontSize: 19,
        fontWeight: 700,
        marginTop: 38,
        marginBottom: 12,
        color: "#111827",
        scrollMarginTop: 24,
    },
    p: {
        fontSize: 15,
        color: "#374151",
        lineHeight: 1.7,
        margin: "0 0 14px",
    },
    ul: {
        paddingLeft: 20,
        margin: "0 0 14px",
    },
    li: {
        fontSize: 15,
        color: "#374151",
        lineHeight: 1.7,
        marginBottom: 6,
    },
    a: {
        color: "#2563eb",
        textDecoration: "none",
        borderBottom: "1px solid rgba(37,99,235,0.35)",
    },
    contactBox: {
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "20px 24px",
        marginTop: 8,
    },
    divider: {
        border: "none",
        borderTop: "1px solid #eef0f3",
        margin: "36px 0 0",
    },
    footer: {
        textAlign: "center",
        fontSize: 12.5,
        color: "#9ca3af",
        padding: "28px 20px 0",
    },
};

const Section = ({ id, title, children }) => (
    <section id={id}>
        <h2 style={styles.h2}>{title}</h2>
        {children}
    </section>
);

const TermsOfService = () => {
    return (
        <div style={styles.page}>
            <div style={styles.hero}>
                <span style={styles.heroBadge}>Trackleads · Hexbis</span>
                <h1 style={styles.heroTitle}>Terms of Service</h1>
                <p style={styles.heroSub}>Effective date: August 31, 2026</p>
            </div>

            <div style={styles.container}>
                <div style={styles.card}>
                    <p style={styles.lead}>
                        These Terms of Service ("Terms") govern your access to and use of{" "}
                        <strong>Trackleads</strong>, a customer relationship management (CRM) platform
                        provided by <strong>Hexbis</strong> ("we", "our", "us"). By accessing or using
                        Trackleads, you agree to be bound by these Terms.
                    </p>

                    <hr style={styles.divider} />

                    <Section id="acceptance" title="1. Acceptance of Terms">
                        <p style={styles.p}>
                            By creating an account, accepting an invitation, or otherwise using Trackleads,
                            you confirm that you have read, understood, and agree to these Terms. If you do
                            not agree, do not use the application.
                        </p>
                    </Section>

                    <Section id="who-can-use" title="2. Who Can Use Trackleads">
                        <p style={styles.p}>
                            Trackleads is a business tool intended for use by organizations and their
                            authorized team members. Accounts are created either directly by an
                            organization or through an invitation sent by an existing team member. You must
                            be at least 18 years old to use Trackleads.
                        </p>
                    </Section>

                    <Section id="accounts" title="3. Accounts &amp; Access">
                        <ul style={styles.ul}>
                            <li style={styles.li}>
                                You are responsible for maintaining the confidentiality of your login
                                credentials and for all activity under your account.
                            </li>
                            <li style={styles.li}>
                                Where you sign in using Google, you authorize Trackleads to use your Google
                                profile information for authentication as described in our Privacy Policy.
                            </li>
                            <li style={styles.li}>
                                Organization administrators may invite, manage, and remove team member
                                accounts within their own workspace.
                            </li>
                        </ul>
                    </Section>

                    <Section id="acceptable-use" title="4. Acceptable Use">
                        <p style={styles.p}>You agree not to:</p>
                        <ul style={styles.ul}>
                            <li style={styles.li}>Use Trackleads for any unlawful purpose or in violation of any applicable law.</li>
                            <li style={styles.li}>Attempt to gain unauthorized access to another organization's data or account.</li>
                            <li style={styles.li}>Use the Gmail-sending feature to send spam, unsolicited, or abusive email.</li>
                            <li style={styles.li}>Interfere with or disrupt the integrity or performance of the application.</li>
                            <li style={styles.li}>Reverse engineer, copy, or resell the platform without authorization.</li>
                        </ul>
                    </Section>

                    <Section id="google-integration" title="5. Google Account Integration">
                        <p style={styles.p}>
                            Trackleads may request permission to send email on your behalf (via the{" "}
                            <code>gmail.send</code> scope) solely to deliver team invitation emails that you
                            explicitly initiate. This access is used strictly for that purpose, as described
                            in our{" "}
                            <a href="/privacy-policy" style={styles.a}>
                                Privacy Policy
                            </a>
                            . You may revoke this access at any time from your Google Account permissions
                            settings.
                        </p>
                    </Section>

                    <Section id="data-ownership" title="6. Data Ownership">
                        <p style={styles.p}>
                            Data you or your organization enter into Trackleads (leads, contacts,
                            quotations, reports, and related business records) remains the property of your
                            organization. We do not claim ownership over your business data and use it only
                            to provide the service.
                        </p>
                    </Section>

                    <Section id="availability" title="7. Availability &amp; Changes">
                        <p style={styles.p}>
                            We aim to keep Trackleads available and reliable but do not guarantee
                            uninterrupted access. Features may be added, changed, or removed over time as
                            the product evolves.
                        </p>
                    </Section>

                    <Section id="termination" title="8. Termination">
                        <p style={styles.p}>
                            We may suspend or terminate access to Trackleads for any account found to be in
                            violation of these Terms, including misuse of email-sending features. An
                            organization may also request removal of its account and associated data at any
                            time by contacting us.
                        </p>
                    </Section>

                    <Section id="disclaimer" title="9. Disclaimer &amp; Limitation of Liability">
                        <p style={styles.p}>
                            Trackleads is provided "as is" without warranties of any kind, express or
                            implied. To the fullest extent permitted by law, Hexbis shall not be liable for
                            any indirect, incidental, or consequential damages arising from use of the
                            application.
                        </p>
                    </Section>

                    <Section id="changes-to-terms" title="10. Changes to These Terms">
                        <p style={styles.p}>
                            We may update these Terms from time to time. Continued use of Trackleads after
                            changes are posted constitutes acceptance of the updated Terms. Material changes
                            will be reflected by updating the "Effective date" above.
                        </p>
                    </Section>

                    <Section id="contact" title="11. Contact Us">
                        <p style={styles.p}>
                            If you have questions about these Terms, contact us at:
                        </p>
                        <div style={styles.contactBox}>
                            <p style={{ ...styles.p, marginBottom: 4 }}>
                                <strong>Hexbis</strong>
                            </p>
                            <p style={{ ...styles.p, marginBottom: 0 }}>
                                Email:{" "}
                                <a href="mailto:alisha.hexbis@gmail.com" style={styles.a}>
                                    alisha.hexbis@gmail.com
                                </a>
                            </p>
                        </div>
                    </Section>
                </div>
            </div>

            <p style={styles.footer}>© 2026 Hexbis · Trackleads</p>
        </div>
    );
};

export default TermsOfService;
